import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MessageSquare, Filter, Search, Calendar, User, Star } from 'lucide-react';
import DashboardLayout from '../DashboardLayout';

const ComentariosEvaluacion = () => {
  const [comentarios, setComentarios] = useState([]);
  const [estadisticas, setEstadisticas] = useState({
    total_comentarios: 0,
    comentarios_lider_empleado: 0,
    comentarios_empleado_lider: 0,
    promedio_puntuacion: 0
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    loadComentarios();
    loadEstadisticas();
  }, [page, filtroTipo]);

  const loadComentarios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dashboardToken');
      
      if (!token) {
        toast.error('No hay sesión activa');
        return;
      }

      let url = `${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/comentarios/evaluaciones?page=${page}&per_page=20`;
      
      if (filtroTipo) {
        url += `&tipo_evaluacion=${filtroTipo}`;
      }
      
      if (busqueda) {
        url += `&search=${encodeURIComponent(busqueda)}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        toast.error('Sesión expirada. Redirigiendo al login...');
        localStorage.removeItem('dashboardToken');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setComentarios(data.items || []);
        setTotalPages(data.pages || 1);
      } else {
        toast.error('Error al cargar comentarios');
      }
    } catch (error) {
      // console.error('Error:', error);
      toast.error('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const token = localStorage.getItem('dashboardToken');
      
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001/api'}/comentarios/estadisticas`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEstadisticas(data);
      }
    } catch (error) {
      // console.error('Error al cargar estadísticas:', error);
    }
  };

  const handleBuscar = () => {
    setPage(1);
    loadComentarios();
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoEvaluacionLabel = (tipo) => {
    return tipo === 'lider_empleado' ? 'Líder → Empleado' : 'Empleado → Líder';
  };

  const getPuntuacionColor = (puntuacion) => {
    if (puntuacion >= 4) return 'text-green-600';
    if (puntuacion >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading && comentarios.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Comentarios de Evaluaciones</h1>
            <p className="mt-2 text-sm text-gray-700">
              Revisa todos los comentarios realizados en las evaluaciones
            </p>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MessageSquare className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Comentarios</dt>
                    <dd className="text-lg font-semibold text-gray-900">{estadisticas.total_comentarios}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <User className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Líder → Empleado</dt>
                    <dd className="text-lg font-semibold text-gray-900">{estadisticas.comentarios_lider_empleado}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <User className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Empleado → Líder</dt>
                    <dd className="text-lg font-semibold text-gray-900">{estadisticas.comentarios_empleado_lider}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Star className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Puntuación Promedio</dt>
                    <dd className="text-lg font-semibold text-gray-900">{estadisticas.promedio_puntuacion}/5</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros y Búsqueda */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="inline h-4 w-4 mr-1" />
                Tipo de Evaluación
              </label>
              <select
                value={filtroTipo}
                onChange={(e) => {
                  setFiltroTipo(e.target.value);
                  setPage(1);
                }}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
              >
                <option value="">Todas</option>
                <option value="lider_empleado">Líder → Empleado</option>
                <option value="empleado_lider">Empleado → Líder</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="inline h-4 w-4 mr-1" />
                Buscar
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
                  placeholder="Buscar por nombre o comentario..."
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                />
                <button
                  onClick={handleBuscar}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Buscar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Comentarios */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {comentarios.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No hay comentarios</h3>
              <p className="mt-1 text-sm text-gray-500">
                No se encontraron comentarios con los filtros seleccionados
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {comentarios.map((comentario) => (
                <div key={comentario.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header del comentario */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            comentario.tipo_evaluacion === 'lider_empleado' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {getTipoEvaluacionLabel(comentario.tipo_evaluacion)}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {comentario.categoria}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatFecha(comentario.fecha_evaluacion)}
                        </div>
                      </div>

                      {/* Pregunta */}
                      <h3 className="text-sm font-medium text-gray-900 mb-2">
                        {comentario.pregunta}
                      </h3>

                      {/* Comentario */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-3">
                        <p className="text-sm text-gray-700 italic">
                          "{comentario.comentarios}"
                        </p>
                      </div>

                      {/* Información de evaluador y evaluado */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Evaluador:</span>
                          <p className="font-medium text-gray-900">{comentario.evaluador.nombre}</p>
                          <p className="text-gray-500 text-xs">{comentario.evaluador.cargo}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Evaluado:</span>
                          <p className="font-medium text-gray-900">{comentario.evaluado.nombre}</p>
                          <p className="text-gray-500 text-xs">{comentario.evaluado.cargo}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Puntuación:</span>
                          <p className={`font-bold text-lg ${getPuntuacionColor(comentario.puntuacion)}`}>
                            {comentario.puntuacion}/5
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Página <span className="font-medium">{page}</span> de <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ComentariosEvaluacion;
